import React, { useState, Fragment, ChangeEvent, useRef, FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { Send, MoveLeft, MoveRight, MessageCircle } from 'lucide-react';

import { useSaveUserMessage } from '../../../hooks/mbts-ux';

import { EChatSource } from '../../../types/entities.enum';

import { TextInput } from '../../shared/Inputs';
import { ChatMessage } from './ChatMessage';
import { useMBTSConversation } from './ConversationWrapper';

export const MBTSChatConvoView = () => {
    const {
        scenarioId,
        conversationId,
        personaDetails,
        isLoadingChats,
        chats,
        setChats,
        messageThreshold,
        isProcessingOverview,
        nextOperation,
        nextPersonaDetails,
        evaluateMessageThreshold,
        isHandleNextDisabled,
        handleNext,
    } = useMBTSConversation();

    const lastMessageRef = useRef<HTMLDivElement>(null);
    const userTypingRef = useRef<HTMLDivElement>(null);
    const personaTypingRef = useRef<HTMLDivElement>(null);

    const [message, setMessage] = useState<string>('');
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [isGeneratingResponse, setIsGeneratingResponse] = useState<boolean>(false);

    const { mutate: saveUserMessage } = useSaveUserMessage();

    const setBottomScroll = () => {
        if (lastMessageRef.current) {
            lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        };

        if (userTypingRef.current) {
            userTypingRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        };

        if (personaTypingRef.current) {
            personaTypingRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        };
    };

    const onChatMessageChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (isSending || isGeneratingResponse) return;

        setIsTyping(true);
        setMessage(e.target.value);
    };

    const onChatMessageBlur = () => {
        setTimeout(() => {
            setIsTyping(false);
        }, 500);
    };

    const onChatMessageSend = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!message?.trim()?.length || !conversationId || isSending || isGeneratingResponse || messageThreshold === 'level2') return;

        saveUserMessage({
            payload: {
                conversation_id: conversationId,
                message,
            },
            setIsTyping,
            setIsSending,
            chats,
            setChats,
            setMessage,
            setIsGeneratingResponse,
            setBottomScroll,
            evaluateMessageThreshold,
        });
    };

    return (
        <Fragment>
            <div className='card card--md card--radius-md mbts_chat_convo__view'>
                <div className='mbts_chat_convo__view__top'>
                    <div className='mbts_chat_convo__view__top__icon'>
                        <MessageCircle />
                    </div>
                    <div>
                        <p className='text-xl text-bold'>{personaDetails.person}</p>
                        <p className='color--txt-secondary mb-xs'>{personaDetails.title}</p>
                        <p className='text-sm color--txt-disabled'>{personaDetails.description}</p>
                    </div>
                </div>
                <div className='mbts_chat_convo__view__body'>
                    {isLoadingChats && !chats.length && (
                        <div className='d-flex flex-column align-items-center justify-content-center gap-sm'>
                            <div className='list-loader' />
                            <p className='text-medium color--txt-secondary'>Loading chats</p>
                        </div>
                    )}
                    {!!chats?.length && (
                        <div className='mbts_chat__messages'>
                            {chats.map((chat, index) => (
                                <ChatMessage
                                    key={index}
                                    {...chat}
                                    custRef={index === chats.length - 1 ? lastMessageRef : undefined}
                                    persona={{ name: personaDetails.person, role: personaDetails.title }}
                                />
                            ))}
                            {isGeneratingResponse && (
                                <ChatMessage
                                    by={EChatSource.PERSONA}
                                    isTyping={true}
                                    custRef={personaTypingRef}
                                    persona={{ name: personaDetails.person, role: personaDetails.title }}
                                />
                            )}
                            {isTyping && (
                                <ChatMessage
                                    by={EChatSource.PLAYER}
                                    isTyping={true}
                                    custRef={userTypingRef}
                                    persona={{ name: personaDetails.person, role: personaDetails.title }}
                                />
                            )}
                        </div>
                    )}
                </div>
                <div className='mbts_chat_convo__view__bottom'>
                    <form onSubmit={onChatMessageSend}>
                        <TextInput
                            type='text'
                            placeholder='Type your message here...'
                            value={message}
                            onChange={onChatMessageChange}
                            onBlur={onChatMessageBlur}
                        />
                        <button
                            type='submit'
                            disabled={!message?.trim()?.length || messageThreshold === 'level2'}
                            className='btn btn--md btn--radius-md btn--primary'
                        >
                            {isSending ? <span className='btn-loader' /> : <Send />}
                            {isSending ? <span>Sending</span> : <span>Send</span>}
                        </button>
                    </form>
                </div>
            </div>
            <div className='mbts_chat_convo__actions'>
                <Link to={`/mbts/${scenarioId}/personas`} className='link link--primary link--flex'>
                    <MoveLeft />
                    <span>Back to Overview</span>
                </Link>
                <button
                    className='btn btn--md btn--radius-md btn--primary'
                    disabled={isHandleNextDisabled()}
                    onClick={handleNext}
                >
                    {isProcessingOverview
                        ? <span className='btn-loader' />
                        : <Fragment>
                            {nextOperation === 'analysis' ? <span>View&nbsp;Analysis</span> : !!nextPersonaDetails ? <span>Next&nbsp;Persona</span> : <span>Go&nbsp;to&nbsp;Overview</span>}
                        </Fragment>
                    }
                    <MoveRight />
                </button>
            </div>
        </Fragment>
    );
};
